/**
 * Pure helpers for the editor's blame (hooks/useEditorCollab.js) over vfs-service's `GET /collab/blame` answer:
 * `lines: [{ line, sha, timestamp, author, via, aiModel, agent, uncommitted }]` (one per line, 1-based).
 */
import { whoLabel } from './changesModel';

const sameBlock = (a, b) => (a.uncommitted && b.uncommitted) || (Boolean(a.sha) && a.sha === b.sha);

/** "2 min ago", "3 h ago", "yesterday", "12 Oct". */
export function ago(iso, now = Date.now()) {
  if (!iso) { return ''; }
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (seconds < 60) { return 'just now'; }
  if (seconds < 3600) { return `${Math.round(seconds / 60)} min ago`; }
  if (seconds < 86400) { return `${Math.round(seconds / 3600)} h ago`; }
  if (seconds < 2 * 86400) { return 'yesterday'; }
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** The short label at the start of a block: "Luca · 2 h ago", "Flow chat for you · 5 min ago", "not saved yet". */
export function blameLabel(entry, you, now = Date.now()) {
  if (!entry || entry.uncommitted) { return 'not saved yet'; }
  return `${whoLabel({ author: entry.author, via: entry.via }, you)} · ${ago(entry.timestamp, now)}`;
}

/** The hover (Markdown): who, when, typed or which agent and model, the commit. */
export function blameHover(entry, you) {
  if (!entry || entry.uncommitted) { return 'Changed here and not saved yet.'; }
  const when = entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '';
  const who = entry.author?.name || 'Someone';
  const how = entry.via
    ? `**${whoLabel({ author: entry.author, via: entry.via }, you)}**${entry.aiModel ? ` · ${entry.aiModel}` : ''}`
    : `Typed by **${who}**`;
  return `${how}  \n${when}${entry.sha ? ` · \`${entry.sha.slice(0, 8)}\`` : ''}`;
}

/** Consecutive lines of one change as blocks: `{ start, end, entry, label(you) }`. */
export function blameBlocks(lines) {
  const blocks = [];
  (lines || []).forEach((entry) => {
    const last = blocks[blocks.length - 1];
    if (last && last.end === entry.line - 1 && sameBlock(last.entry, entry)) {
      last.end = entry.line;
    } else {
      blocks.push({ start: entry.line, end: entry.line, entry, label: (you, now) => blameLabel(entry, you, now) });
    }
  });
  return blocks;
}
